import { datadogLogs } from "@datadog/browser-logs";
import { ImsStatus } from "@redotech/crdb-enums/inventory/ims-status";
import { RateSource } from "@redotech/money/rate-source";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { LoadState } from "@redotech/react-util/load";
import { RedoShopifyDeliveryMethodType } from "@redotech/redo-fulfillment-group-common/fulfillment-delivery-method-type";
import {
  FulfillmentGroupData,
  FulfillmentGroupPickStatus,
  FulfillmentService,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  FulfillmentGroupStatus,
  OpenLikeStatuses,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { primaryCarrierShipment } from "@redotech/redo-fulfillment-group-common/shipment-summary";
import { ShopifyFulfillmentHoldDisplayNames } from "@redotech/redo-fulfillment-group-common/shopify-fulfillment-schemas";
import { HidePriceActionButtons } from "@redotech/redo-fulfillment-outbound-labels-common/fulfillment-group-actions";
import { FulfillmentGroupActions } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import DotIcon from "@redotech/redo-icons/dot-01.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonGroup } from "@redotech/redo-web/arbiter-components/buttons/redo-button-group";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { downloadUrl } from "@redotech/redo-web/download";
import { Flex } from "@redotech/redo-web/flex";
import { MoneyText } from "@redotech/redo-web/fx/money-text";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import { useRpcMutation } from "@redotech/redo-web/rpc-query/use-rpc-mutation";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ActionButtonMessage } from "../../common/action-button-message";
import { FulfillmentGroupActionsButton } from "../../common/fulfillment-group-actions-button";
import {
  PinVerificationType,
  usePinVerification,
} from "../../common/pin-verification-context";
import { ReshipModal } from "../../common/reship-modal";
import { ShipmentFlagsBadge } from "../../common/shipment-flags-badge";
import { OutboundRateOption } from "../../common/util";
import { useShipmentFlags } from "../../hooks/use-shipment-flags";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { shouldShowConsolidationInvoiceButtons } from "../../utils/consolidation-invoice-utils";
import { getEffectiveShipmentSummary } from "../../utils/effective-shipment-summary";
import { canRequestGetNewRates } from "../../utils/outbound-labels-utils";
import {
  GroupsAssignedState,
  useFulfillmentGroupPermissions,
} from "../../utils/use-assigned-fulfillment-service";
import { useSelectedRate } from "../../utils/use-group-info";

export const FulfillmentActionsCard = memo(function FulfillmentActionsCard({
  actionLoad,
  displayedButtonAction,
  errorMessage,
  insufficientFunds,
  fulfillmentGroups,
  performAction,
  selectedStatus,
  canPurchaseEveryGroup,
  purchasableGroups,
  showVoidLabelButton,
  showCancelFreightButton,
  showCancelManualFulfillmentButton,
  totalRatePrice,
  triggerVoidShipmentLoad,
  triggerCancelFreight,
  triggerCancelManualFulfillmentLoad,
  updatesPending,
  voidShipmentLoad,
  cancelFreightLoad,
  cancelManualFulfillmentLoad,
  warningMessage,
  printShortcutsDisabled,
  actionsDisabled = false,
  actionsDisabledReason,
  onActionButtonClick,
  triggerCreateDuplicateShipment,
  duplicateShipmentLoad,
  handleUpdates,
  everythingDisabled,
  triggerRefreshRates,
  useDefaultActionButton,
  voidConfirmationMessage,
  showCostSummary = false,
}: {
  actionLoad: LoadState<void>;
  displayedButtonAction: FulfillmentGroupActions;
  errorMessage: string;
  insufficientFunds: boolean;
  fulfillmentGroups: readonly FulfillmentGroupData[];
  performAction: (action: FulfillmentGroupActions) => void;
  selectedStatus: FulfillmentGroupStatus | null | undefined;
  canPurchaseEveryGroup: boolean;
  purchasableGroups: readonly FulfillmentGroupData[];
  showVoidLabelButton: boolean;
  showCancelFreightButton: boolean;
  showCancelManualFulfillmentButton: boolean;
  totalRatePrice: number | null;
  triggerVoidShipmentLoad: () => void;
  triggerCancelFreight: () => void;
  triggerCancelManualFulfillmentLoad: () => void;
  updatesPending: boolean;
  voidShipmentLoad: LoadState<void>;
  cancelFreightLoad: LoadState<void>;
  cancelManualFulfillmentLoad: LoadState<void>;
  warningMessage: string;
  printShortcutsDisabled?: boolean;
  actionsDisabled?: boolean;
  actionsDisabledReason?: string;
  onActionButtonClick?: () => void;
  triggerCreateDuplicateShipment: (lineItemExternalIds?: string[]) => void;
  duplicateShipmentLoad: LoadState<void>;
  handleUpdates: (updates: FulfillmentGroupUpdate[]) => void;
  everythingDisabled: boolean;
  triggerRefreshRates: () => void;
  useDefaultActionButton: boolean;
  voidConfirmationMessage: string;
  showCostSummary?: boolean;
}) {
  const [reshipModalOpen, setReshipModalOpen] = useState(false);
  const [holdModalOpen, setHoldModalOpen] = useState(false);
  const openHoldModal = useCallback(() => setHoldModalOpen(true), []);
  const closeHoldModal = useCallback(() => setHoldModalOpen(false), []);

  const firstGroup = fulfillmentGroups[0];
  const holds = firstGroup?.holds;
  const isOnHold =
    firstGroup?.status === FulfillmentGroupStatus.OnHold &&
    holds &&
    holds.length > 0;
  const firstHoldReason = isOnHold
    ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ShopifyFulfillmentHoldDisplayNames[holds[0]!.reason]
    : undefined;

  const holdBadge = isOnHold ? (
    <Flex cursor="pointer" onClick={openHoldModal}>
      <RedoBadge
        color="warning"
        segmentLeading={{ type: "icon", Icon: DotIcon }}
        size="xs"
        text={
          <>
            On hold:{" "}
            <Text as="span" textDecoration="underline">
              {firstHoldReason}
            </Text>
          </>
        }
        type="primary"
      />
    </Flex>
  ) : null;
  const { executeWithPinCheck } = usePinVerification();
  const navigate = useNavigate();
  const routes = useRequiredContext(MerchantRoutesContext);

  const viewSubmittedClaim = useHandler((claimId: string) => {
    navigate(
      `${routes.getClaimsSubRoute("carrier-claims")}?selected=${claimId}`,
    );
  });

  const singleFulfillmentGroup =
    fulfillmentGroups.length === 1 ? fulfillmentGroups[0] : undefined;
  const isProblemOrder =
    singleFulfillmentGroup?.pickStatus === FulfillmentGroupPickStatus.PROBLEM;

  const navigateToProblemOrderDetails = useHandler(() => {
    if (singleFulfillmentGroup) {
      navigate(
        `/stores/${team?._id}/order-management/outbound-labels/pick-sessions/problem-order/${singleFulfillmentGroup._id}`,
      );
    }
  });

  const voidLabelButtonText =
    fulfillmentGroups.length > 1
      ? `Void ${fulfillmentGroups.length} labels`
      : "Void label";

  const cancelManualFulfillmentButtonText =
    fulfillmentGroups.length > 1
      ? `Cancel ${fulfillmentGroups.length} fulfillments`
      : "Cancel fulfillment";

  const anyGroupPending = fulfillmentGroups.some(
    (g) => g.status === FulfillmentGroupStatus.Pending,
  );

  const showReshipButton = fulfillmentGroups.every(
    (group) => group.status === FulfillmentGroupStatus.Closed,
  );

  // If they didn't buy a label in Redo, it doesn't make sense to call it reship - it's just duplicating the shipment
  const reshipButtonText = fulfillmentGroups.some(
    (fg) => primaryCarrierShipment(getEffectiveShipmentSummary(fg))?.purchased,
  )
    ? "Reship"
    : "Duplicate fulfillment";

  const reshipButtonDisabled = fulfillmentGroups.length > 1;

  const isLocalPickup = fulfillmentGroups.every(
    (fg) => fg.deliveryMethod === RedoShopifyDeliveryMethodType.PickUp,
  );

  const showPrice =
    !isLocalPickup &&
    !HidePriceActionButtons.includes(displayedButtonAction) &&
    !showReshipButton;

  const { groupsAssignedState, linkedFulfillmentServices } =
    useFulfillmentGroupPermissions(fulfillmentGroups);

  const team = useRequiredContext(TeamContext);

  const { selectedRate } = useSelectedRate(fulfillmentGroups, true);
  const flags = useShipmentFlags(fulfillmentGroups, selectedRate, team);

  const isOnIms =
    team.settings.ims?.enabled &&
    team.settings.ims.status === ImsStatus.SourceOfTruth;

  const assignToTeam = useHandler(async () => {
    handleUpdates(
      fulfillmentGroups.map((g) => ({
        fulfillmentGroupId: g._id,
        assignedFulfillmentService: g.teamInfo,
      })),
    );
  });

  const assignToFulfillmentService = useHandler(
    async (service: { _id: string; name: string }) => {
      handleUpdates(
        fulfillmentGroups.map((g) => ({
          fulfillmentGroupId: g._id,
          assignedFulfillmentService: service,
        })),
      );
    },
  );

  const canGetNewRates = useMemo(() => {
    return fulfillmentGroups.some((group) => canRequestGetNewRates(group));
  }, [fulfillmentGroups]);

  const onVoidLabel = useHandler(() => {
    executeWithPinCheck(
      () => {
        datadogLogs.logger.info("Void label confirmed", {
          fulfillmentGroupIds: fulfillmentGroups.map(({ _id }) => _id),
          teamId: team?._id,
          source: "fulfillment-app",
          action: "Void label",
        });
        triggerVoidShipmentLoad();
      },
      { type: PinVerificationType.VoidLabel },
      {
        title: "Void Label PIN Verification",
        description: "Please enter your PIN to proceed with voiding the label.",
      },
      {
        title: voidLabelButtonText,
        description:
          voidConfirmationMessage ||
          (fulfillmentGroups.length > 1
            ? "Please confirm that you want to void these labels"
            : "Please confirm that you want to void this label"),
        confirmText: voidLabelButtonText,
        theme: "error",
      },
    );
  });

  const onCancelManualFulfillment = useHandler(() => {
    executeWithPinCheck(
      () => {
        datadogLogs.logger.info("Cancel manual fulfillment confirmed", {
          fulfillmentGroupIds: fulfillmentGroups.map(({ _id }) => _id),
          teamId: team?._id,
          source: "fulfillment-app",
          action: "Cancel fulfillment",
        });
        triggerCancelManualFulfillmentLoad();
      },
      { type: PinVerificationType.MarkFulfilled },
      {
        title: "Cancel Fulfillment PIN Verification",
        description:
          "Please enter your PIN to proceed with cancelling the fulfillment.",
      },
      {
        title: cancelManualFulfillmentButtonText,
        description:
          fulfillmentGroups.length > 1
            ? "This reopens these orders for fulfillment and notifies the marketplace. Inventory stays deducted."
            : "This reopens this order for fulfillment and notifies the marketplace. Inventory stays deducted.",
        confirmText: cancelManualFulfillmentButtonText,
        theme: "error",
      },
    );
  });

  const showConsolidationInvoice =
    fulfillmentGroups.length === 1 &&
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    shouldShowConsolidationInvoiceButtons(fulfillmentGroups[0]!);

  const csvMutation = useRpcMutation(RedoOutboundLabelsRpcClientContext, {
    method: "getConsolidationInvoiceCsv",
    options: {
      onSuccess: ({ url }) => {
        downloadUrl("consolidation_invoice.csv", url);
      },
      onError: () => {
        toast("Failed to download invoice CSV", { variant: "error" });
      },
    },
  });

  const pdfMutation = useRpcMutation(RedoOutboundLabelsRpcClientContext, {
    method: "getConsolidationInvoicePdf",
    options: {
      onSuccess: ({ url }) => {
        window.open(url, "_blank", "noopener,noreferrer");
      },
      onError: () => {
        toast("Failed to download invoice PDF", { variant: "error" });
      },
    },
  });

  const downloadConsolidationInvoiceCsv = useHandler(() => {
    if (fulfillmentGroups.length !== 1) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    csvMutation.mutate({ fulfillmentGroupId: fulfillmentGroups[0]!._id });
  });

  const downloadConsolidationInvoicePdf = useHandler(() => {
    if (fulfillmentGroups.length !== 1) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    pdfMutation.mutate({ fulfillmentGroupId: fulfillmentGroups[0]!._id });
  });

  const showFulfillmentServiceAction =
    linkedFulfillmentServices.length > 0 && canPurchaseEveryGroup;
  const showPriceOrFlags = showPrice || flags.length > 0;
  const showErrors =
    errorMessage ||
    warningMessage ||
    insufficientFunds ||
    fulfillmentGroups.some(
      ({ status, buyLabelErrors, buyReturnLabelErrors }) =>
        OpenLikeStatuses.includes(status) &&
        (buyLabelErrors?.length || buyReturnLabelErrors?.length),
    );

  return (
    <>
      <Flex
        bgColor="tertiary"
        dir="column"
        gap="none"
        justify="center"
        w="full"
      >
        {(showFulfillmentServiceAction || showPriceOrFlags || showErrors) && (
          <Flex dir="column" gap="xl" px="3xl" py="xl" w="full">
            {showFulfillmentServiceAction && (
              <FulfillmentServiceAction
                assignToFulfillmentService={assignToFulfillmentService}
                assignToTeam={assignToTeam}
                fulfillmentGroups={fulfillmentGroups}
                groupsAssignedState={groupsAssignedState}
                linkedFulfillmentServices={linkedFulfillmentServices}
              />
            )}
            {showPriceOrFlags && (
              <Flex
                align="center"
                dir="row"
                gap="sm"
                justify="space-between"
                w="full"
              >
                {showPrice && (
                  <>
                    {showCostSummary ? (
                      <ActionLabelCost
                        canGetNewRates={canGetNewRates}
                        fulfillmentGroupsLength={fulfillmentGroups.length}
                        selectedRate={selectedRate}
                        totalRatePrice={totalRatePrice}
                        triggerRefreshRates={triggerRefreshRates}
                      />
                    ) : (
                      <Flex dir="column" gap="none">
                        <Text
                          data-testid="total-rate-price"
                          fontSize="sm"
                          fontWeight="semibold"
                        >
                          {totalRatePrice != null ? (
                            <MoneyText
                              source={RateSource.Stripe}
                              value={toMoney({
                                amount: totalRatePrice,
                                currency: selectedRate?.currency,
                                struck: selectedRate?.struck,
                              })}
                            />
                          ) : (
                            "-"
                          )}
                        </Text>
                        {canGetNewRates && (
                          <Tooltip
                            placement="top"
                            title={
                              fulfillmentGroups.length > 1
                                ? "Refreshing rates for many fulfillments at once may take a few minutes"
                                : ""
                            }
                          >
                            <div>
                              <Text
                                cursor="pointer"
                                fontSize="xs"
                                fontWeight="thin"
                                hoverTextColor="primary"
                                onClick={() => {
                                  triggerRefreshRates();
                                }}
                                pt="none"
                                textColor="secondary"
                                textDecoration="underline"
                              >
                                Get updated rates
                              </Text>
                            </div>
                          </Tooltip>
                        )}
                      </Flex>
                    )}
                  </>
                )}
                {flags.length > 0 && (
                  <Flex align="center" gap="sm" grow={1} justify="flex-end">
                    <ShipmentFlagsBadge flags={flags} />
                  </Flex>
                )}
              </Flex>
            )}
            <ActionButtonMessage
              anyGroupPending={anyGroupPending}
              clearBuyLabelErrorsOnRender
              errorMessage={errorMessage}
              fulfillmentGroups={fulfillmentGroups}
              insufficientFunds={insufficientFunds}
              warningMessage={warningMessage}
            />
            {holdBadge}
          </Flex>
        )}
        <Tooltip
          placement="top"
          title={actionsDisabled ? (actionsDisabledReason ?? "") : ""}
        >
          <Flex
            borderColor="primary"
            borderStyle="solid"
            borderTopWidth="1px"
            dir="column"
            gap="none"
            w="full"
          >
            <FulfillmentGroupActionsButton
              actionsDisabled={actionsDisabled}
              buttonSize="3xl"
              canPurchaseEveryGroup={canPurchaseEveryGroup}
              displayedButtonAction={displayedButtonAction}
              errorMessage={errorMessage}
              everythingDisabled={everythingDisabled}
              fulfillmentGroups={fulfillmentGroups}
              insufficientFunds={insufficientFunds}
              loading={actionLoad.pending || updatesPending}
              onActionButtonClick={onActionButtonClick}
              onClick={performAction}
              printShortcutsDisabled={printShortcutsDisabled}
              purchasableGroups={purchasableGroups}
              selectedStatus={selectedStatus}
              squared
              useDefaultActionButton={useDefaultActionButton}
              viewSubmittedClaim={viewSubmittedClaim}
              warningMessage={warningMessage}
            />
          </Flex>
        </Tooltip>

        <RedoButtonGroup squared>
          {isProblemOrder && (
            <RedoButton
              flexGrow={1}
              hierarchy="secondary"
              onClick={navigateToProblemOrderDetails}
              size="2xl"
              squared
              text="View problem order details"
              theme="normal"
            />
          )}
          {showVoidLabelButton && (
            <RedoButton
              disabled={everythingDisabled}
              flexGrow={1}
              hierarchy="secondary"
              onClick={onVoidLabel}
              pending={voidShipmentLoad.pending}
              size="2xl"
              squared
              text={voidLabelButtonText}
              theme="destructive"
            />
          )}
          {showCancelManualFulfillmentButton && (
            <RedoButton
              disabled={everythingDisabled}
              flexGrow={1}
              hierarchy="secondary"
              onClick={onCancelManualFulfillment}
              pending={cancelManualFulfillmentLoad.pending}
              size="2xl"
              squared
              text={cancelManualFulfillmentButtonText}
              theme="destructive"
            />
          )}
          {showCancelFreightButton && (
            <RedoButton
              disabled={everythingDisabled}
              flexGrow={1}
              hierarchy="secondary"
              onClick={() => triggerCancelFreight()}
              pending={cancelFreightLoad.pending}
              size="2xl"
              squared
              text="Cancel freight"
              theme="destructive"
            />
          )}
          {showReshipButton && (
            <RedoButton
              disabled={everythingDisabled || reshipButtonDisabled}
              flexGrow={1}
              hierarchy="secondary"
              onClick={() =>
                executeWithPinCheck(
                  () => setReshipModalOpen(true),
                  { type: PinVerificationType.Reship },
                  {
                    title: "Reship PIN Verification",
                    description:
                      "Please enter your PIN to proceed with reshipping these items.",
                  },
                )
              }
              pending={duplicateShipmentLoad.pending}
              size="2xl"
              squared
              text={reshipButtonText}
              theme="normal"
            />
          )}
        </RedoButtonGroup>
        {showConsolidationInvoice && (
          <RedoButtonGroup squared>
            <RedoButton
              flexGrow={1}
              hierarchy="secondary"
              onClick={downloadConsolidationInvoiceCsv}
              pending={csvMutation.isPending}
              size="2xl"
              squared
              text="Invoice CSV"
              theme="normal"
            />
            <RedoButton
              flexGrow={1}
              hierarchy="secondary"
              onClick={downloadConsolidationInvoicePdf}
              pending={pdfMutation.isPending}
              size="2xl"
              squared
              text="Invoice PDF"
              theme="normal"
            />
          </RedoButtonGroup>
        )}
        <ReshipModal
          isOnIms={!!isOnIms}
          isOpen={reshipModalOpen}
          items={
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            fulfillmentGroups.length === 1 ? fulfillmentGroups[0]!.items : []
          }
          onCancel={() => setReshipModalOpen(false)}
          onSuccess={(selectedLineItemExternalIds) => {
            setReshipModalOpen(false);
            triggerCreateDuplicateShipment(selectedLineItemExternalIds);
          }}
        />
      </Flex>
      {isOnHold && (
        <RedoModal
          isOpen={holdModalOpen}
          onModalCloseRequested={closeHoldModal}
          primaryButton={{ text: "Close", onClick: closeHoldModal }}
          title="Hold Details"
        >
          <Flex flexDirection="column" gap="lg">
            {holds.map((hold, i) => (
              <Flex flexDirection="column" gap="xs" key={hold.holdHandle || i}>
                <Text fontSize="sm" fontWeight="semibold">
                  {ShopifyFulfillmentHoldDisplayNames[hold.reason]}
                </Text>
                {hold.reasonNotes && (
                  <Text fontSize="sm" textColor="secondary">
                    {hold.reasonNotes}
                  </Text>
                )}
              </Flex>
            ))}
          </Flex>
        </RedoModal>
      )}
    </>
  );
});

const ActionLabelCost = memo(function ActionLabelCost({
  canGetNewRates,
  fulfillmentGroupsLength,
  selectedRate,
  totalRatePrice,
  triggerRefreshRates,
}: {
  canGetNewRates: boolean;
  fulfillmentGroupsLength: number;
  selectedRate: OutboundRateOption | null;
  totalRatePrice: number | null;
  triggerRefreshRates: () => void;
}) {
  return (
    <Flex dir="column" gap="none">
      <Text data-testid="total-rate-price" fontSize="sm" fontWeight="semibold">
        {totalRatePrice != null ? (
          <MoneyText
            source={RateSource.Stripe}
            value={toMoney({
              amount: totalRatePrice,
              currency: selectedRate?.currency,
              struck: selectedRate?.struck,
            })}
          />
        ) : (
          "-"
        )}
      </Text>
      {canGetNewRates && (
        <Tooltip
          placement="top"
          title={
            fulfillmentGroupsLength > 1
              ? "Refreshing rates for many fulfillments at once may take a few minutes"
              : ""
          }
        >
          <div>
            <Text
              cursor="pointer"
              fontSize="xs"
              fontWeight="thin"
              hoverTextColor="primary"
              onClick={triggerRefreshRates}
              pt="none"
              textColor="secondary"
              textDecoration="underline"
            >
              Get updated rates
            </Text>
          </div>
        </Tooltip>
      )}
    </Flex>
  );
});

// We only care to show this to the merchant - the 3PL doesn't need to see it
const FulfillmentServiceAction = memo(function FulfillmentServiceAction({
  groupsAssignedState,
  linkedFulfillmentServices,
  fulfillmentGroups,
  assignToTeam,
  assignToFulfillmentService,
}: {
  groupsAssignedState: GroupsAssignedState;
  linkedFulfillmentServices: FulfillmentService[];
  fulfillmentGroups: readonly FulfillmentGroupData[];
  assignToTeam: () => void;
  assignToFulfillmentService: (service: { _id: string; name: string }) => void;
}) {
  const assignedToText = useMemo(() => {
    if (groupsAssignedState === GroupsAssignedState.MyStore) {
      return "My store";
    }

    if (
      groupsAssignedState === GroupsAssignedState.DifferentFulfillmentService
    ) {
      const assignedServiceId =
        fulfillmentGroups[0]?.assignedFulfillmentService?._id;
      const matchingService = linkedFulfillmentServices.find(
        (s) => s._id === assignedServiceId,
      );
      return matchingService?.name ?? "Fulfillment service";
    }

    return "Mixed";
  }, [groupsAssignedState, linkedFulfillmentServices, fulfillmentGroups]);

  if (groupsAssignedState === GroupsAssignedState.MyStore) {
    return null;
  }

  return (
    <Flex dir="column" gap="xxs" justify="space-between" w="full">
      <Text fontSize="xs">Assigned to</Text>
      <Text fontSize="xs" fontWeight="semibold">
        {assignedToText}
      </Text>
      {groupsAssignedState ===
        GroupsAssignedState.DifferentFulfillmentService && (
        <Text
          cursor="pointer"
          fontSize="xs"
          fontWeight="thin"
          onClick={() => {
            assignToTeam();
          }}
          textColor="secondary"
          textDecoration="underline"
        >
          Assign to my store
        </Text>
      )}
    </Flex>
  );
});
